import React from "react";
import { DatePicker, InputNumber, Modal, Select, Space, Typography } from "antd";
import type { Dayjs } from "dayjs";
import type { GlobalToken } from "antd/es/theme/interface";
import { DATE_TIME_FORMAT, TIME_INPUT_FORMAT } from "./constants";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

type DrinkOption = {
    value: number;
    label: string;
};

type EditActivityModalProps = {
    open: boolean;
    isSaving: boolean;
    token: GlobalToken;
    drinkOptions: DrinkOption[];
    loadingDrinks: boolean;
    selectedDrinkId: number | null;
    onClose: () => void;
    onSubmit: () => void;
    onDrinkChange: (value: number) => void;
    amount: number;
    onAmountChange: (value: number) => void;
    timestamp: Dayjs | null;
    onTimestampChange: (value: Dayjs | null) => void;
    durationMinutes: number;
    onDurationChange: (value: number) => void;
    startTimestamp: Dayjs | null;
    onStartChange: (value: Dayjs | null) => void;
};

const EditActivityModal: React.FC<EditActivityModalProps> = ({
    open,
    isSaving,
    token,
    drinkOptions,
    loadingDrinks,
    selectedDrinkId,
    onClose,
    onSubmit,
    onDrinkChange,
    amount,
    onAmountChange,
    timestamp,
    onTimestampChange,
    durationMinutes,
    onDurationChange,
    startTimestamp,
    onStartChange,
}) => {
    const { t } = useTranslation();

    return (
        <Modal
            rootClassName="drink-edit-modal"
            open={open}
            title={t("drinkStats.editModal.title")}
            onCancel={onClose}
            onOk={onSubmit}
            confirmLoading={isSaving}
            okText={t("drinkStats.editModal.save")}
            destroyOnHidden
            styles={{
                body: {
                    background: token.colorBgElevated,
                    color: token.colorText,
                },
            }}
            style={{
                background: token.colorBgElevated,
            }}
        >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <div>
                    <Text strong><label htmlFor="drink-edit-select">{t("drinkStats.editModal.drinkLabel")}</label></Text>
                    <Select
                        id="drink-edit-select"
                        showSearch
                        placeholder={t("drinkStats.editModal.drinkPlaceholder")}
                        optionFilterProp="label"
                        options={drinkOptions}
                        value={selectedDrinkId ?? undefined}
                        onChange={(value) => onDrinkChange(typeof value === "number" ? value : Number(value))}
                        loading={loadingDrinks}
                        disabled={drinkOptions.length === 0}
                    />
                </div>
                <div>
                    <Text strong>{t("drinkStats.editModal.amountLabel")}</Text>
                    <InputNumber
                        aria-label={t("drinkStats.editModal.amountLabel")}
                        min={1}
                        style={{ width: "100%" }}
                        value={amount}
                        onChange={(value) => onAmountChange(value ?? 0)}
                    />
                </div>
                <div>
                    <Text strong>{t("drinkStats.editModal.finishedLabel")}</Text>
                    <DatePicker
                        style={{ width: "100%" }}
                        showTime={{ format: TIME_INPUT_FORMAT }}
                        value={timestamp}
                        onChange={onTimestampChange}
                        format={DATE_TIME_FORMAT}
                    />
                </div>
                <div>
                    <Text strong>{t("drinkStats.editModal.startedLabel")}</Text>
                    <DatePicker
                        style={{ width: "100%" }}
                        showTime={{ format: TIME_INPUT_FORMAT }}
                        value={startTimestamp}
                        onChange={onStartChange}
                        format={DATE_TIME_FORMAT}
                        disabled={!timestamp}
                    />
                </div>
                <div>
                    <Text strong>{t("drinkStats.editModal.durationLabel")}</Text>
                    <InputNumber
                        aria-label={t("drinkStats.editModal.durationLabel")}
                        min={0}
                        max={600}
                        style={{ width: "100%" }}
                        value={durationMinutes}
                        addonAfter={t("drinkStats.editModal.durationSuffix")}
                        onChange={(value) => onDurationChange(value ?? 0)}
                    />
                    {timestamp && (
                        <Text type="secondary">
                            {durationMinutes === 0
                                ? t("drinkStats.editModal.chuggedNote")
                                : t("drinkStats.editModal.startedAround", {
                                      time: timestamp
                                          .subtract(durationMinutes || 0, "minute")
                                          .format("DD MMM YYYY HH:mm"),
                                  })}
                        </Text>
                    )}
                </div>
            </Space>
        </Modal>
    );
};

export default EditActivityModal;
